/**
 * Statsig Event Constants
 *
 * This file contains all event name constants and related string literals.
 * Naming Convention:
 * - User actions: act:event_name (e.g., act:play_game)
 * - System events: evt:event_name (e.g., evt:promo_bonus_expired)
 */

// ===== EXPERIMENT CONSTANTS =====
export const EXPERIMENT_VALUES = {
  PRIZES: "Prizes",
  PROMOTION: "promotion",
} as const;

export const EVENTS = {
  // ===== REGISTRATION FLOW =====
  CLICK_REGISTER: "act:click_register",
  REGISTER_FORM_SUBMIT: "act:register_form_submit",
  OTP_REQUEST: "act:otp_request",
  OTP_REQUEST_RESULT: "evt:otp_request_result",
  OTP_VERIFY_RESULT: "evt:otp_verify_result",
  REGISTRATION_RESULT: "evt:registration_result",
  REGISTRATION_FORM_FOCUS: "act:registration_form_focus",

  // ===== DEPOSIT FLOW =====
  CLICK_DEPOSIT: "act:click_deposit",
  SELECT_DEPOSIT_METHOD_GROUP: "act:select_deposit_method_group",
  SELECT_DEPOSIT_METHOD_INDIVIDUAL: "act:select_deposit_method_individual",
  SELECT_DEPOSIT_AMOUNT: "act:select_deposit_amount",
  TOP_UP_SUBMIT: "act:top_up_submit",
  DEPOSIT_CREATE_RESULT: "evt:deposit_create_result",
  AUTO_FAST_TRIGGERED: "evt:auto_fast_triggered",
  DEPOSIT_SUCCESS: "evt:deposit_success",
  DEPOSIT_FAILED: "evt:deposit_failed",
  CLOSE_DEPOSIT_IFRAME: "act:close_deposit_iframe",
  OPEN_DEPOSIT_IFRAME: "act:open_deposit_iframe",
  OPEN_DEPOSIT_CUSTOM: "act:open_deposit_custom",
  CLOSE_DEPOSIT_CUSTOM: "act:close_deposit_custom",

  // Empty states tracking (system events)
  DEPOSIT_GROUPS_EMPTY: "evt:deposit_groups_empty",
  DEPOSIT_METHODS_EMPTY: "evt:deposit_methods_empty",

  // ===== GAMEPLAY =====
  GAME_OPEN_TRIGGERED: "act:game_open_triggered",
  GAME_OPENED: "act:game_opened",
  GAME_CLOSED: "act:game_closed",
  WALLET_UPDATED: "evt:wallet_updated",
  GAME_ERROR: "evt:game_error",
  SESSION_TIMEOUT: "evt:session_timeout",
  GAME_UNAVAILABLE: "evt:game_unavailable",
  GAME_SEARCH: "act:game_search",
  GAME_SEARCH_SELECT: "act:game_search_select",

  // ===== PROMOTIONS =====
  PROMO_CLAIM_ATTEMPT: "act:promo_claim_attempt",
  PROMO_CLAIM_SUCCESS: "evt:promo_claim_success",
  PROMO_REWARD_APPLIED: "evt:promo_reward_applied",
  PROMO_BONUS_EXPIRED: "evt:promo_bonus_expired",
  CLICK_MINI_BANNER: "act:click-mini-banner",
  CLICK_HOME_BANNER: "act:click-home-banner",
  CLICK_PROMOTION_PAGE: "act:click-promotion-page",
  CLICK_PROMOTION_ITEM: "act:click-promotion-item",
  CASHBACK_MINI_PROMPT_SHOWN: "evt:cashback-mini-prompt-shown",
  CLAIM_CASHBACK_MINI_PROMPT: "act:claim-cashback-mini-prompt",

  // ===== WITHDRAWALS =====
  CLICK_WITHDRAWAL: "act:click_withdrawal",
  WITHDRAWAL_SUBMIT: "act:withdrawal_submit",
  WITHDRAWAL_SUCCESS: "evt:withdrawal_success",
  WITHDRAWAL_FAILED: "evt:withdrawal_failed",

  // ===== NOTIFICATIONS =====
  NOTIFICATION_INAPP_PROMPT_SHOWN: "evt:notification_inapp_prompt_shown",
  NOTIFICATION_INAPP_PROMPT_RESPONSE: "act:notification_inapp_prompt_response",
  NOTIFICATION_BROWSER_PROMPT_SHOWN: "evt:notification_browser_prompt_shown",
  NOTIFICATION_BROWSER_PROMPT_RESPONSE: "act:notification_browser_prompt_response",
  NOTIFICATION_SUBSCRIPTION_STATUS: "evt:notification_subscription_status",

  // ===== LANGUAGE =====
  CHANGE_LANGUAGE: "act:change_language",

  // ===== LEGACY/GENERAL =====
  CLICK_LOGIN: "act:click_login",
  LOGIN_FORM_SUBMIT: "act:login_form_submit",
  LOGIN: "act:login",
  LOGIN_FAILED: "evt:login_failed",
  LOGOUT: "act:logout",
  UPDATE_PROFILE: "act:update_profile",
  PAGE_VIEW: "act:page_view",
  SESSION_STARTED: "evt:session_started",
  SESSION_ENDED: "evt:session_ended",
  API_ERROR: "evt:api_error",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

// Game event sources
export const GAME_SOURCE = {
  SEARCH: "search",
} as const;

export type GameSource = (typeof GAME_SOURCE)[keyof typeof GAME_SOURCE];

export const FEATURE_FLAGS = {
  BOTTOM_NOTIFICATION_DRAWER: "bottom_notification_drawer",
} as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

export const EXPERIMENTS = {
  mvpChromeNotificationBottomDrawer: {
    name: "mvp_chrome_notification_bottom_drawer",
    parameters: {
      bottomNotificationDrawerEnabled: "bottom_notification_drawer_enabled",
    },
  },
  prizesToPromotion: {
    name: "prizes_to_promotion_wording",
    parameters: {
      wording: "prizes_button_to_promotion",
    },
  },
  forgotButtonPosition: {
    name: "forgot_pin_password_button_enhancement",
    parameters: {
      showProminentButton: "forgot_pin_password_button_move",
    },
  },
  unclaimedInstantCashbackMiniPrompt: {
    name: "unclaimed_instant_cashback_mini_prompt",
    parameters: {
      unclaimedInstantCashbackMiniPrompt: "unclaimed_instant_cashback_mini_prompt",
    },
  },
  improvedPromotionWordings: {
    name: "promotion_rewording",
    parameters: {
      useImprovedWordings: "promotion_page_reword",
    },
  },
  registrationGiftIndicators: {
    name: "registration_gift_indicators",
    parameters: {
      showGiftBox: "show_gift_box",
      showFreeGiftsBadge: "show_free_gifts_badge",
    },
  },
  openPaymentPageNewTab: {
    name: "open_payment_page_new_tab",
    parameters: {
      depositOpenNewTab: "deposit_open_new_tab",
    },
  },
} as const;

export type Experiment = (typeof EXPERIMENTS)[keyof typeof EXPERIMENTS];

export const STATSIG_AVAILABLE_EVENT = "statsig:available";
export const STATSIG_READY_EVENT = "statsig:ready";
export const REGISTRATION_FORM_FOCUS_SESSION_KEY = "statsig:registration_form_focus_fired";
